import {
  checkConsent,
  getConsentLegalDocumentContent,
  submitConsent,
} from '@api/selfServiceServices';
import ImprivataLogo from '@assets/Imprivata_Logo.svg?react';
import { useSession } from '@common/authorization/context/Session';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { AppError } from '@errorHandler/errors';
import { ConsentType } from '@imprivata-cloud/authn';
import { Button, Modal } from '@imprivata-cloud/components';
import { Consent } from '@imprivata-cloud/consent';
import { useEnrollFace } from '@selfservice/hooks/useEnrollFace';
import { useSelfServiceData } from '@selfservice/utils/useSelfServiceData';
import { Flex, Spin } from 'antd';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RootRoutes } from '@/index';
import { useNavigateKeepSearch } from '@hooks/useNavigateKeepSearch';
import { isFaceEnabled } from '@/self-service/utils/helpers';
import { EnrollmentCompletedCard } from '../EnrollFace/EnrollmentCompletedCard/EnrollmentCompletedCard';
import '../../styles.less';
import './FaceCapture.less';
import { EnrollFlow } from '../EnrollFace/EnrollFlow/EnrollFlow';
import { EventProvider } from '@imprivata-cloud/core';

const normalizeConsentGrantType = (grantType: string): '' | 'granted' | 'not-granted' => {
  if (grantType === '' || grantType === 'granted' || grantType === 'not-granted') {
    return grantType;
  }
  return '';
};

const FaceCapture: React.FC = () => {
  const { t } = useTranslation();

  const { userInfo } = useSession();
  const { data, isLoading, isError, error } = useSelfServiceData();
  const { emitError } = useErrorContext();

  const navigateKeepSearch = useNavigateKeepSearch();

  const {
    onConsentComplete,
    onSuccessfulFaceEnrollment,
    onCancel,
    onEnrollmentDone,
    isModalOpen,
    screenState,
  } = useEnrollFace();

  const consentInput = useMemo(
    () => ({
      consentType: ConsentType.BIOMETRICS,
      residenceLocations: data?.residenceLocations ?? [],
      userUpn: userInfo?.userDisplayName ? userInfo.userDisplayName : userInfo?.userUpn,
    }),
    [data?.residenceLocations, userInfo?.userDisplayName, userInfo?.userUpn],
  );

  const consentPorts = useMemo(
    () => ({
      checkConsent: async (input: { consentType: string; userUpn?: string }) => {
        const response = await checkConsent(input);
        return { ...response, grantType: normalizeConsentGrantType(response.grantType) };
      },
      submitConsent: async (input: {
        grantType: 'granted' | 'not-granted' | '';
        stateOfResidence: string;
        documentNameList: string[];
        userUpn?: string;
      }) =>
        submitConsent({
          consent: {
            consentType: ConsentType.BIOMETRICS,
            residenceLocationShortName: input.stateOfResidence,
            grantType: input.grantType,
            documentNameList: input.documentNameList,
            userId: '',
          },
          userUpn: input.userUpn,
        }),
      fetchDocument: getConsentLegalDocumentContent,
    }),
    [],
  );

  useEffect(() => {
    // Wait for both data and camera check to complete before redirecting
    if (!isLoading && !isFaceEnabled(data)) {
      navigateKeepSearch(RootRoutes.SELF_SERVICE);
    }
  }, [data, isLoading, navigateKeepSearch]);

  if (isError) {
    if (error instanceof AppError) {
      emitError(error);
    } else {
      emitError(AppError.fromMessage('Error getting self service data'));
    }
  }

  const renderContent = () => {
    if (screenState === 'welcome') {
      return (
        <Consent
          input={consentInput}
          ports={consentPorts}
          translate={(key, fallback, params) => t(key, { defaultValue: fallback, ...params })}
          onComplete={onConsentComplete}
          onCancel={onCancel}
          onError={message => emitError(AppError.fromMessage(message))}
        />
      );
    }

    if (screenState === 'face-enrolled') {
      return <EnrollmentCompletedCard onDone={onEnrollmentDone} />;
    }

    return null;
  };

  if (isLoading) {
    return (
      <div className="enroll-content">
        <Flex align="center" justify="center">
          <Spin />
        </Flex>
      </div>
    );
  }

  return (
    <div className="enroll-content">
      <Modal
        open={isModalOpen && screenState === 'enrollment'}
        onCancel={onCancel}
        closeIcon={false}
        className="face-enroll-modal"
        destroyOnHidden
        wrapClassName="enroll-modal-wrapper"
        maskProps={{ style: { backgroundColor: 'rgba(255, 255, 255, 0.5)' } }}
        footer={
          <div className="enroll-footer">
            <div className="enroll-cancel-button">
              <Button aria-label="enroll-cancel-button" type="text" onClick={onCancel}>
                {t('common.cancel')}
              </Button>
            </div>
            <div className="enroll-footer-logo">
              <ImprivataLogo />
            </div>
          </div>
        }
        maskClosable={false}
      >
        <div className="enroll-container">
          <EventProvider>
            <EnrollFlow
              userInfo={userInfo}
              data={data}
              onSuccessfulFaceEnrollment={onSuccessfulFaceEnrollment}
            />
          </EventProvider>
        </div>
      </Modal>

      {renderContent()}
    </div>
  );
};

export default FaceCapture;
